import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import UserSettings from 'components/Layouts/UserSettings';
import { LogoUrl, MixpanelEventName } from 'utils/constants';
import { FetchContext, useHttp } from 'hooks/use-fetch';
import Chip from '@mui/material/Chip';
import { useNavigate, Link } from 'react-router-dom';
import { ProviderPractice } from 'pages/SuperAdmin/types';
import Tooltip from '@mui/material/Tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Box, IconButton, keyframes, useMediaQuery } from '@mui/material';
import NotificationsDrawer from 'pages/Dashboard/NotificationsDrawer';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useQuery } from 'react-query';
import { healthRecordRefreshUrls } from 'utils/apiUrls';
import { unreadNotificationsCount } from 'pages/Dashboard/services/healthrecords.services';
import { trackMixpanelEvent } from 'utils/utilMethods';
import InlineNavItems from 'components/Layouts/HeaderNav';
import * as spaUrl from 'utils/spaUrls';
import { UserRole } from 'types/NavTreeModel';
import DemoModeBanner from 'components/Common/DemoBanner';
import { PersonAdd } from '@mui/icons-material';
import { mainNav } from 'utils/spaUrls';

const bounceKeyframe = keyframes`
  0% {
    transform: rotate(0deg);
  }
  15% {
    transform: rotate(10deg);
  }
  30% {
    transform: rotate(-10deg);
  }
  45% {
    transform: rotate(5deg);
  }
  60% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
`;

const REFRESH_INTERVAL = 5000;
const REFRESH_DURATION = 2000;

export default function Header() {
  const theme = useTheme();
  const navigator = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    activeProvider,
    superAdminId,
    updateActiveProvider,
    userRoles,
    isDemoUser,
  } = React.useContext(FetchContext);
  const [showNotificationsDrawer, setShowNotificationsDrawer] = React.useState(false);
  const { http, providerId, isHrRefreshEnabled, providerName, selectedProviderName } = useHttp();
  const [refreshing, setRefreshing] = React.useState(false);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), REFRESH_DURATION);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const isChiefAdmin = !!userRoles[UserRole.CHIEF_ADMIN];
  const isSuperAdmin = !!superAdminId;

  const shouldFetchNotifications = !!providerId && isHrRefreshEnabled;

  const notificationsCount = useQuery({
    queryKey: [healthRecordRefreshUrls.unreadNotifications.queryUrl, providerId],
    queryFn: unreadNotificationsCount(http.get),
    enabled: shouldFetchNotifications,
    refetchInterval: shouldFetchNotifications ? 120000 : false,
  });

  React.useEffect(() => {
    if (shouldFetchNotifications) {
      const interval = setInterval(() => {
        notificationsCount.refetch();
      }, 120000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [notificationsCount, shouldFetchNotifications]);

  const handleActiveProviderClick = () => {
    updateActiveProvider({ providerId: superAdminId } as ProviderPractice);
    navigator('/providers-list');
  };

  const openNotificationDrawer = () => {
    setShowNotificationsDrawer(true);
    trackMixpanelEvent(MixpanelEventName.HEADER_NOTIFICATION_BELL_CLICKED, {
      'unread count': (notificationsCount.data?.notifications.unread ?? 0).toString(),
      'provider id': providerId ?? '',
      'logged in provider name': providerName ?? '',
      'selected provider name': selectedProviderName ?? '',
    });
  };

  const toggleRecordRefreshDrawer = (open: boolean) => {
    setShowNotificationsDrawer(open);
  };

  const headerHeight = `calc(${theme.custom.appBarHeight} + ${!isChiefAdmin ? theme.custom.navBarHeight : '0px'} + ${
    isDemoUser ? theme.custom.demoBarHeight : '0px'
  })`;

  return (
    <>
      <NotificationsDrawer
        isOpen={showNotificationsDrawer}
        toggleDrawer={toggleRecordRefreshDrawer}
      />
      <AppBar
        position='fixed'
        sx={{
          height: headerHeight,
          justifyContent: 'center',
          background: theme.palette.background.default,
          boxShadow: 'none',
        }}
      >
        {isDemoUser ? <DemoModeBanner /> : null}
        <Toolbar
          disableGutters
          sx={{
            height: headerHeight,
          }}
        >
          <Box width='100%'>
            <Box
              sx={{
                maxWidth: '1500px',
                margin: '0 auto',
                px: { xs: 2, md: 3 },
              }}
            >
              <Grid container justifyContent='space-between'>
                <Grid item display='flex' alignItems='center'>
                  <input
                    type='image'
                    height={theme.custom.logoHeight}
                    width={theme.custom.logoWidth}
                    src={LogoUrl.lg}
                    alt='Logo'
                    onClick={() => navigator(spaUrl.mainNav.dashboard)}
                  />
                </Grid>
                <Grid item display='flex' alignItems='center'>
                  {isSuperAdmin && activeProvider?.providerName && (
                    <Tooltip title='Change active provider'>
                      <Chip
                        sx={{
                          mr: 3,
                        }}
                        color='primary'
                        label={(
                          <div>
                            <b>Active provider :</b>
                            {' '}
                            {activeProvider.providerName}
                          </div>
                        )}
                        variant='outlined'
                        onClick={handleActiveProviderClick}
                      />
                    </Tooltip>
                  )}
                  <Grid item display='flex' alignItems='center' gap={{ xs: 2, md: 4.5 }}>
                    {!isChiefAdmin && !isSuperAdmin && (
                      <Button
                        color='primary'
                        variant='contained'
                        size='small'
                        type='submit'
                        component={Link}
                        startIcon={<PersonAdd />}
                        to={mainNav.addNewPatient}
                        sx={{
                          height: 40,
                          px: isMobile ? 1 : 2,
                          minWidth: isMobile ? 40 : undefined,
                          borderRadius: isMobile ? '50%' : undefined,
                          fontSize: '15px',
                          '& .MuiButton-startIcon': { mx: isMobile ? 0 : undefined },
                        }}
                        onClick={() => {
                          trackMixpanelEvent(
                            MixpanelEventName.DASHBOARD_ADD_PATIENTS_BUTTON_CLICKED,
                            {},
                          );
                        }}
                      >
                        {isMobile ? '' : 'ADD PATIENTS'}
                      </Button>
                    )}
                    {!isChiefAdmin && shouldFetchNotifications
                      && !notificationsCount.isLoading
                      && notificationsCount.data
                      && ((notificationsCount?.data?.notifications?.unread as number) > 0 ? (
                        <IconButton sx={{ p: 0.5 }} onClick={openNotificationDrawer}>
                          <Badge
                            badgeContent={notificationsCount.data?.notifications.unread}
                            color='error'
                            showZero={false}
                            sx={{
                              '& .MuiBadge-badge': {
                                marginTop: '9px',
                                marginRight: '9px',
                              },
                            }}
                          >
                            <NotificationsIcon
                              sx={{
                                color: '#0000008A',
                                animation: refreshing
                                  ? `${bounceKeyframe} 1s ease-in-out infinite`
                                  : 'none',
                              }}
                              fontSize='large'
                            />
                          </Badge>
                        </IconButton>
                      ) : (
                        <IconButton sx={{ p: 0.5 }} onClick={openNotificationDrawer}>
                          <NotificationsNoneOutlinedIcon
                            sx={{ color: '#0000008A' }}
                            fontSize='medium'
                          />
                        </IconButton>
                      ))}
                    <UserSettings />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Toolbar>
        {!isChiefAdmin && (
        <Box
          display='flex'
          alignItems='center'
          minHeight={theme.custom.navBarHeight}
          bgcolor={theme.custom.colors.backgroundColorSecondary}
        >
          <Box maxWidth={1500} margin='0 auto' width='100%' px={3} gap={3} display='flex' alignItems='center' justifyContent='flex-start'>
            <InlineNavItems />
          </Box>
        </Box>
        )}
      </AppBar>
    </>
  );
}
